import type { BaseQueryApi, FetchArgs } from '@reduxjs/toolkit/query';
import { createApi } from '@reduxjs/toolkit/query/react';

import { ENV } from '@constants/environment';
import { baseQueryCreator } from '@utils/baseQueryCreator';

export type PlayerReportingApiName = 'bonusApi' | 'bonusService';

export interface IPlayerReportingApiExtraOptions {
  shouldUseBonusService?: boolean;
}

export interface IPlayerReportingApiUrl {
  VITE_BONUS_API_URL: string;
  VITE_BONUS_SERVICE_API_URL: string;
  VITE_PLAYER_REPORTING_SERVICE_API_URL: string;
}

export function definePlayerReportingBaseUrl(
  parameters: {
    apiName: PlayerReportingApiName;
    shouldUseBonusService?: boolean;
  },
  environment: IPlayerReportingApiUrl = ENV
): string {
  const { apiName, shouldUseBonusService } = parameters;

  if (!shouldUseBonusService) {
    return environment.VITE_PLAYER_REPORTING_SERVICE_API_URL;
  }

  return apiName === 'bonusApi'
    ? environment.VITE_BONUS_API_URL
    : environment.VITE_BONUS_SERVICE_API_URL;
}

export function createPlayerReportingApi(
  parameters: Readonly<{
    reducerPath: string;
    apiName: PlayerReportingApiName;
  }>
) {
  const { apiName, reducerPath } = parameters;

  return createApi({
    reducerPath,
    endpoints: () => ({}),
    refetchOnReconnect: true,
    baseQuery: (
      arguments_: FetchArgs,
      api: BaseQueryApi,
      extraOptions: IPlayerReportingApiExtraOptions
    ) => {
      const service = definePlayerReportingBaseUrl({
        apiName,
        shouldUseBonusService: extraOptions?.shouldUseBonusService,
      });

      return baseQueryCreator(service)(arguments_, api, extraOptions);
    },
  });
}

export const playerReportingBonusApiSlice = createPlayerReportingApi({
  apiName: 'bonusApi',
  reducerPath: 'playerReportingBonusApi',
});

export const playerReportingBonusServiceApiSlice = createPlayerReportingApi({
  apiName: 'bonusService',
  reducerPath: 'playerReportingService',
});
